"use client";

import FlightList from "@/components/frontend-components/flight-folder/flight-list/list";
import PublicShareJourneyHeader from "@/components/frontend-components/flight-folder/flight-list/PublicShareJourneyHeader";
import WebLogo from "@/components/common/WebLogo";
import { PageActionsProvider } from "@/contexts/PageActionsContext";
import { usePublicFlightShare } from "@/hooks/flight/usePublicFlightShare";
import { getFlightData } from "@/lib/flight/getFlightData";
import useFlightBookingStore from "@/lib/useFlightBookingStore";
import Image from "next/image";
import { notFound, useParams } from "next/navigation";
import React, { useEffect } from "react";

const UUID_V4_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const PUBLIC_SHARE_PAGE_ACTIONS_USER = { id: null, role: null };

export default function PublicShareFlightPage() {
  const params = useParams();
  const shareId = params.shareId as string;

  if (!shareId || !UUID_V4_REGEX.test(shareId)) {
    notFound();
  }

  const { metadata, flightData, isLoading, error, isExpired, loadShare } =
    usePublicFlightShare(shareId);
  const { resetStore, setFilteredFlights } = useFlightBookingStore();

  useEffect(() => {
    resetStore();
    loadShare();
  }, [shareId, loadShare, resetStore]);

  useEffect(() => {
    if (flightData) {
      setFilteredFlights(flightData);
    }
  }, [flightData, setFilteredFlights]);

  const flightListProps = flightData
    ? getFlightData(flightData)
    : {
        onward: [],
        returnFlight: undefined,
        multiCity: {},
        combo: undefined,
      };

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/5 backdrop-blur-sm">
        <Image
          src={"/plane-loading.gif"}
          width={100}
          height={100}
          alt="loading"
          className="transform -scale-x-100 rounded-full bg-[#084A76]"
        />
      </div>
    );
  }

  return (
    <PageActionsProvider pageActions={[]} user={PUBLIC_SHARE_PAGE_ACTIONS_USER}>
      <div className="min-h-screen bg-[#f1f5f8]">
        <header className="bg-white border-b">
          <div className="px-4 py-3">
            <WebLogo />
          </div>
          {metadata && !error && (
            <PublicShareJourneyHeader
              searchQuery={metadata.searchQuery as any}
              metadata={(metadata.metadata as any[]) ?? []}
              tripType={metadata.tripType}
            />
          )}
        </header>

        <main className="container mx-auto px-3 py-6">
          {error ? (
            <div className="bg-white rounded-lg shadow-lg max-w-2xl mx-auto p-8 text-center">
              <h1 className="text-xl font-semibold text-gray-900 mb-2">
                {isExpired ? "Link Expired" : "Unable to Load Flights"}
              </h1>
              <p className="text-gray-600">{error}</p>
            </div>
          ) : metadata && flightData ? (
            Object.keys(flightData?.searchResult || {}).length > 0 ? (
              <FlightList
                {...flightListProps}
                routeData={metadata.searchQuery}
                metadata={metadata.metadata}
                tripType={metadata.tripType}
                readOnly
                shareId={shareId}
              />
            ) : (
              <div className="bg-white rounded-lg shadow-lg max-w-2xl mx-auto p-8 text-center">
                <h2 className="text-lg font-semibold mb-2">No Flights Found</h2>
                <p className="text-gray-600">
                  No flights are currently available for this search.
                </p>
              </div>
            )
          ) : null}
        </main>
      </div>
    </PageActionsProvider>
  );
}
